import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { PapersTable } from '../../../components/committee/papers-table';
import { AssignEvaluatorsDialog } from '../../../components/committee/assign-evaluators-dialog';
import { PaperDocumentDialog } from '../../../components/committee/paper-document-dialog';
import { ReassignEvaluatorsDialog } from '../../../components/committee/reassign-evaluators-dialog';
import {
  type CommitteePaperFilter,
  type CommitteePaperItem,
  useCommitteePapers,
  useStartSecondRound,
} from '@/hooks/use-committee-papers';

interface CommitteePapersPageProps {
  eventId: string;
}

const FILTERS: Array<{ key: CommitteePaperFilter; label: string }> = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Pendientes' },
  { key: 'UNDER_EVAL', label: 'En evaluacion' },
  { key: 'APPROVED', label: 'Aprobadas' },
  { key: 'REJECTED', label: 'Rechazadas' },
];

export function CommitteePapersPage({ eventId }: CommitteePapersPageProps) {
  const [filter, setFilter] = useState<CommitteePaperFilter>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [assignPaper, setAssignPaper] = useState<CommitteePaperItem | null>(null);
  const [reassignPaper, setReassignPaper] = useState<CommitteePaperItem | null>(null);
  const [documentPaper, setDocumentPaper] = useState<CommitteePaperItem | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data: papers = [], isLoading, isFetching } = useCommitteePapers(eventId, filter, debouncedSearch);
  const startSecondRoundMutation = useStartSecondRound(eventId);

  const handleStartSecondRound = async (paper: CommitteePaperItem) => {
    try {
      await startSecondRoundMutation.mutateAsync({ paperId: paper.id });
      toast.success('Segunda ronda iniciada correctamente.');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'No se pudo iniciar la segunda ronda.');
    }
  };

  const orderedPapers = useMemo(() => papers, [papers]);

  if (!eventId) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
        Seleccione un evento para gestionar ponencias del comite.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-5xl font-bold text-[#1e3a5f]">Ponencias del Comite</h2>
        <p className="mt-1 text-lg text-gray-600">Gestion de ponencias registradas y asignacion de evaluadores</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
              placeholder="Buscar por titulo, folio o autor..."
            />
          </div>

          <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
            {FILTERS.map((item) => (
              <Button
                key={item.key}
                size="sm"
                onClick={() => setFilter(item.key)}
                className={
                  filter === item.key
                    ? 'bg-[#1e3a5f] text-white hover:bg-[#1e3a5f]'
                    : 'bg-transparent text-gray-700 hover:bg-white'
                }
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>

        <PapersTable
          papers={orderedPapers}
          isLoading={isLoading || isFetching}
          onAssign={(paper) => setAssignPaper(paper)}
          onReassign={(paper) => setReassignPaper(paper)}
          onStartSecondRound={handleStartSecondRound}
          onManageDocument={(paper) => setDocumentPaper(paper)}
        />
      </div>

      <AssignEvaluatorsDialog
        open={Boolean(assignPaper)}
        onOpenChange={(open) => {
          if (!open) setAssignPaper(null);
        }}
        eventId={eventId}
        paper={assignPaper}
      />

      <ReassignEvaluatorsDialog
        open={Boolean(reassignPaper)}
        onOpenChange={(open) => {
          if (!open) setReassignPaper(null);
        }}
        eventId={eventId}
        paper={reassignPaper}
      />

      <PaperDocumentDialog
        open={Boolean(documentPaper)}
        onOpenChange={(open) => {
          if (!open) setDocumentPaper(null);
        }}
        eventId={eventId}
        paper={documentPaper}
      />
    </div>
  );
}
